import get from 'lodash/get';
import trim from 'lodash/trim';
import { useState, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { TextField } from '@numbox/components';
import { Banner, Button, ButtonRow } from '@numbox/components';
import { AuthModule, CLIENT_TYPE } from '@numbox/modules';
import { isValidEmail, testIsAuth0Token } from '@numbox/util';

import CenteredSpinner from '@shared/CenteredSpinner';

import { useAppSelector } from '~/effects/redux';
import { getAuthError } from '~/modules/auth';
import { userLoggedIn, wipeCredentials } from '~/util/auth';
import { urlWithSearch } from '~/util/router';

import { Prompt } from '../../shared/Prompt';
import { PromptContent } from '../../shared/PromptContent';
import { PromptHeader } from '../../shared/PromptHeader';
import { LoginContext } from './LoginContext';
import { useNumaAuth0 } from './auth0Util';
import {
  HAS_ENTERED_PASSWORD_KEY,
  ROUTES,
  getSafeDestination,
} from './constants';

export const EnterEmail = () => {
  const history = useHistory();
  const { attemptSilentAuth0Login } = useNumaAuth0();

  const location = useLocation();
  const desiredDestination = urlWithSearch(
    get(location, 'state.from.pathname') || '',
    get(location, 'state.from.search') || '',
  );

  const [isTyping, setIsTyping] = useState(true);
  const authenticationError = useAppSelector(getAuthError);
  const hasEnteredPasswordBefore = localStorage.getItem(
    HAS_ENTERED_PASSWORD_KEY,
  );

  const { email, setEmail, setDesiredDestination } = useContext(LoginContext);

  const dispatch = useDispatch();

  const [isAttemptingRefresh, setIsAttemptingRefresh] = useState(false);

  useEffect(() => {
    setDesiredDestination(desiredDestination);
    const cookieToken = userLoggedIn();
    const isAuth0Token = testIsAuth0Token(cookieToken);
    if (isAuth0Token) {
      setIsAttemptingRefresh(true);
      attemptSilentAuth0Login({
        onSuccess: () => {
          history.push(getSafeDestination(desiredDestination));
        },
        onFailure: () => {
          wipeCredentials();
          setIsAttemptingRefresh(false);
        },
      });
    } else if (cookieToken) {
      setIsAttemptingRefresh(true);
      dispatch(
        AuthModule.refreshToken.request(
          {
            refreshToken: cookieToken,
            clientType: CLIENT_TYPE,
          },
          {
            onSuccess: () => {
              history.push(getSafeDestination(desiredDestination));
            },
            onFailure: () => {
              setIsAttemptingRefresh(false);
            },
          },
        ),
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = () => {
    if (hasEnteredPasswordBefore === 'true') {
      history.push(`${ROUTES.LOGIN}${ROUTES.ENTER_PASSWORD}`);
    } else {
      history.push(`${ROUTES.LOGIN}${ROUTES.CHOOSE_AUTH}`);
    }
  };
  const isValid = email.length >= 3 && isValidEmail(email);

  if (isAttemptingRefresh) {
    return <CenteredSpinner />;
  }
  return (
    <Prompt data-cy="login-form" variant="auth">
      <PromptHeader
        logoType="icon"
        title="Sign in to Numa"
        subtitle={
          <span>
            Enter your <b>email address</b> to sign in.
          </span>
        }
      />
      {authenticationError ||
        (!isTyping && email.length > 1 && !isValidEmail(email) && (
          <Banner color="warning">
            {authenticationError ||
              'Did you make a typo in your email address?'}
          </Banner>
        ))}
      <PromptContent>
        <TextField
          data-cy="login-email"
          id="login-email"
          name="email"
          type="email"
          placeholder="you@example.com"
          label="Email address"
          autoFocus
          autoComplete="email"
          fullWidth
          value={email}
          onChange={({ target: { value } }) => setEmail(trim(value))}
          onFocus={() => setIsTyping(true)}
          onBlur={() => setIsTyping(false)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              if (isValid) {
                onSubmit();
              }
            }
          }}
        />

        <ButtonRow fullWidth>
          <Button
            isPrimary
            data-cy="login-btn"
            disabled={!isValid}
            onClick={onSubmit}
          >
            Sign in
          </Button>
        </ButtonRow>
      </PromptContent>
    </Prompt>
  );
};
